import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import { AttributeValue } from "../../../Types/Attribute";
import { useDarkMode } from "../../../hooks/useDarkMode";

interface LineChartProps {
    chartData: AttributeValue[];
    chartName: string;
    max?: number;
    uom: string;
    stepped?: boolean;
}

export function LineChart(lineChartProps: LineChartProps) {
    Chart.register(CategoryScale);
    const [options, setOptions] = useState({});
    const [oldestValue, setOldestValue] = useState<AttributeValue | null>(null);
    const [latestValue, setLatestValue] = useState<AttributeValue | null>(null);

    const value = latestValue?.Value.toString() || "";
    const normalizedValue = value.replace(',', '.');
    const numericValue = parseFloat(normalizedValue);
    const roundedValue = !isNaN(numericValue) && normalizedValue
        ? numericValue.toFixed(3) + " " + lineChartProps?.uom
        : "N/A";
    const isDarkTheme = useDarkMode();
    const [data, setData] = useState<{
        labels: string[];
        datasets: {
            label: string;
            data: number[];
            backgroundColor: string[];
            borderColor: string;
            borderWidth: number;
            pointRadius: number;
            fill?: boolean;
            stepped?: boolean;
            tension?: number;
        }[];
    }>({
        labels: [],
        datasets: [
            {
                label: "",
                data: [],
                backgroundColor: [],
                borderColor: "",
                borderWidth: 0,
                pointRadius: 0,
                fill: true,
                stepped: lineChartProps.stepped,
                tension: 0.4
            }
        ]
    });
    useEffect(() => {
        const latestValue = lineChartProps.chartData[lineChartProps.chartData.length - 1] || null;
        const oldestValue = lineChartProps.chartData[0] || null;
        setLatestValue(latestValue);
        setOldestValue(oldestValue);

        const backgroundColor = `hsla(210, 50%, 50%, 0.5)`
        const borderColor = `hsla(210, 10%, 50%, 0.8)`
        setData({
            labels: lineChartProps.chartData.map((point) => point.Utc),
            datasets: [
                {
                    label: "",
                    data: lineChartProps.chartData.map((data) => data.Value as number),
                    backgroundColor: [
                        backgroundColor,
                    ],
                    borderColor: borderColor,
                    borderWidth: 2,
                    pointRadius: 2,
                    fill: true,
                    stepped: lineChartProps.stepped,
                    tension: 0.1
                },
            ],
        });

        const gridColor = isDarkTheme ? "#ffffff" : "#000000";



        setOptions({
            plugins: {
                legend: {
                    display: false,
                },
            },
            responsive: true,
            scales: {
                y: {

                    min: lineChartProps.max ? 0 : undefined,
                    max: lineChartProps.max ? lineChartProps.max : undefined,
                    position: "right",
                    grid: { color: 'grey' },
                    ticks: {
                        color: 'grey'

                    },
                },
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        color: gridColor,
                        display: false

                    },
                },
            },
        });
    }, [lineChartProps.chartData, isDarkTheme]);

    if (!lineChartProps.chartData.length) {
        return null;
    }
    return (
        <div className="px-1">
            <div className="text-text-light dark:text-text-dark font-bold text-base justify-between flex">
                <span >{lineChartProps.chartName}</span>
                <span>{roundedValue}</span>
            </div>
            <Line
                data={data}
                options={options}
            />

            <div className="flex justify-between  text-base px-1">
                <span>
                    {new Date(oldestValue?.Utc || 0).toLocaleString("sv-SE", { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span>

                    {new Date(latestValue?.Utc || 0).toLocaleString("sv-SE", { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
            </div>
        </div>
    );
}